export async function fetchSubscriptionPackageAdminData(api) {
    const [packages, projects] = await Promise.all([
        api.listAdminSubscriptionPackages(),
        api.listSubscriptionPackageProjects(),
    ]);

    return {
        packages: packages.map((item) => ({
            ...item._package,
            ...item,
            isLegacy: item._package?.isLegacy,
            public: item._package?._public ?? false,
        })),
        projects,
    };
}

export const MAX_PUBLIC_SUBSCRIPTION_PACKAGES = 3;

export function countPublicSubscriptionPackages(packages) {
    return packages.filter((pkg) => pkg.public === true && pkg.isLegacy !== true).length;
}

export function subscriptionPackageSortValue(pkg, key) {
    if (key === "defaultOrder") return 0;
    if (key === "package") return pkg.name;
    if (key === "monthlyPrice") return pkg.monthlyCzk;
    if (key === "usage") return pkg.usageCount;
    if (key === "projects") return (pkg.currentProjects ?? []).join(" ");
    return null;
}

function subscriptionPackageGroup(pkg) {
    if (pkg.isLegacy) return pkg.isArchived ? 4 : 3;
    if (pkg.isArchived) return 2;
    return pkg.public ? 0 : 1;
}

export function sortSubscriptionPackagesDefault(packages) {
    return [...packages].sort((left, right) => {
        const groupDifference = subscriptionPackageGroup(left) - subscriptionPackageGroup(right);
        if (groupDifference !== 0) return groupDifference;

        const priceDifference = Number(right.monthlyCzk ?? 0) - Number(left.monthlyCzk ?? 0);
        if (priceDifference !== 0) return priceDifference;

        return String(left.code ?? left.name ?? "").localeCompare(
            String(right.code ?? right.name ?? ""),
            "cs",
            {numeric: true, sensitivity: "base"},
        );
    });
}

export function supportedPackageFeatures(pkg) {
    const features = pkg.features ?? {};
    const support = ["email", "email_chat", "priority_sla"].includes(features.support) ? features.support : "email";
    const marketingPerformance = ["channel_campaign", "campaign_adset_ad"].includes(features.marketing_performance)
        ? features.marketing_performance
        : "channel_campaign";
    const onboarding = ["self_service_review", "validation_included", "personal"].includes(features.onboarding)
        ? features.onboarding
        : "self_service_review";
    const setupSupportMinutes = [0, 30, 60].includes(features.setup_support_minutes) ? features.setup_support_minutes : 0;
    const teamTraining = ["video_docs", "online_training_video_docs"].includes(features.team_training)
        ? features.team_training
        : "video_docs";

    return {
        max_marketing_integrations: Number.isInteger(features.max_marketing_integrations) ? features.max_marketing_integrations : pkg.maxIntegrations,
        additional_cost_sources: features.additional_cost_sources === true
            || (features.emailing === true && features.affiliate === true && features.other_costs === true),
        abc_analysis: features.abc_analysis === true,
        mcp_access: features.mcp_access === true,
        roles_permissions: features.roles_permissions !== false,
        support,
        marketing_performance: marketingPerformance,
        onboarding,
        setup_support_minutes: setupSupportMinutes,
        team_training: teamTraining,
        ...(features.included_consulting === "1h_quarter" ? {included_consulting: "1h_quarter"} : {}),
    };
}

export function createSubscriptionPackageDraft(source = null, allowPublic = true) {
    if (source === null) {
        return {
            code: "",
            maxUsers: 0,
            maxIntegrations: 0,
            maxDomains: 0,
            dataUpdatesPerDay: 0,
            retentionMonths: 0,
            monthlyCzk: 0,
            yearlyCzk: 0,
            monthlyEur: 0,
            yearlyEur: 0,
            public: false,
            isArchived: false,
            availableProjectIds: [],
            features: {
                executive_overview: true,
                sales_orders_aov: true,
                gross_margin_profit: true,
                top_products: true,
                roles_permissions: true,
            },
            internalNote: null,
            isLegacy: false,
        };
    }

    return {
        code: "",
        maxUsers: Number(source.maxUsers ?? 0),
        maxIntegrations: Number(source.maxIntegrations ?? 0),
        maxDomains: Number(source.maxDomains ?? 0),
        dataUpdatesPerDay: Number(source.dataUpdatesPerDay ?? 0),
        retentionMonths: Number(source.retentionMonths ?? 0),
        monthlyCzk: Number(source.monthlyCzk ?? 0),
        yearlyCzk: Number(source.yearlyCzk ?? 0),
        monthlyEur: Number(source.monthlyEur ?? 0),
        yearlyEur: Number(source.yearlyEur ?? 0),
        public: allowPublic && source.public === true,
        isArchived: source.isArchived === true,
        availableProjectIds: (source.availableForProjects ?? []).map((project) => project.id),
        features: supportedPackageFeatures(source),
        internalNote: null,
        isLegacy: false,
    };
}
